'use client';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/material';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GlassTooltip } from '../GlassTooltip';

/** Tone for the inline status dot. Maps to theme palette so it follows the
 * portal's brand. `mint` (= success) for production, `amber` (= warning) for
 * non-prod environments, `red` for incidents, `neutral` for unknown. */
export type BrandStatusTone = 'mint' | 'amber' | 'red' | 'neutral';

export interface BrandStatusDot {
  /** Color tone (default: `'mint'`). */
  tone?: BrandStatusTone;
  /** Tooltip text on hover (e.g. `"Powered by Fiops · LOCAL"`). */
  tooltip?: string;
}

export interface BrandBlockProps {
  /** Primary brand label, e.g. "OpsMatrix Capital". */
  label: string;
  /**
   * Legacy 2-line caption (mono uppercase), e.g. "FIOPS · PROD". Ignored
   * when `statusDot` is provided — v3 uses the dot + tooltip instead.
   */
  caption?: string;
  /**
   * v3 compact env indicator. When present, the block renders the v3
   * layout (single-line label + tiny status dot + optional chevron) and
   * `caption` is dropped. `statusDot.tooltip` carries the verbose text
   * (env name, attribution) onto a hover tooltip so the chrome stays light.
   * Mirrors the v3 design (`shell.jsx:121-134`).
   */
  statusDot?: BrandStatusDot;
  /** Two-letter brand mark text, e.g. "OM". Falls back to first two chars of `label`. */
  initials?: string;
  /** Optional image src — when provided, renders an image instead of initials. */
  logoUrl?: string;
  /** When truthy, renders a workspace-switcher chevron next to the labels. */
  showSwitcher?: boolean;
  /** Click handler — fires on the whole block. Falls back to no-op. */
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

/**
 * BrandBlock — TopBar brand cluster.
 *
 * Two layouts:
 *  - **v3 compact** (when `statusDot` is provided): 22px gradient brand
 *    mark + single-line label + tiny status dot (with tooltip) + optional
 *    chevron. The dot replaces the verbose `caption`. ~196px wide.
 *  - **Legacy verbose** (when `caption` is provided without `statusDot`):
 *    28px square brand mark + two-line label/caption + optional chevron.
 *    Wider (~224px). Kept for tenants and portals that haven't migrated.
 *
 * The OpsMatrix tenant ships with the mint accent; other tenants override
 * `label`, `caption`, `initials`, and `logoUrl` via `business.branding`.
 */
export function BrandBlock({
  label,
  caption,
  statusDot,
  initials,
  logoUrl,
  showSwitcher = false,
  onClick,
  sx,
}: BrandBlockProps) {
  const theme = useTheme();
  const mint = theme.palette.success.main;
  const mintSoft = theme.palette.success.light;
  const isDark = theme.palette.mode === 'dark';
  const resolvedInitials = (initials ?? label.slice(0, 2)).toUpperCase();
  const interactive = Boolean(onClick);
  const compact = statusDot !== undefined;

  // v3 compact mode: gradient mark, single-line label, tiny status dot.
  // Theme palette tones — `mint`→success, `amber`→warning, `red`→error.
  const statusToneColor: Record<BrandStatusTone, string> = {
    mint,
    amber: theme.palette.warning.main,
    red: theme.palette.error.main,
    neutral: theme.palette.text.disabled,
  };
  const dotColor = statusToneColor[statusDot?.tone ?? 'mint'];

  // Brand mark: 22px (compact) or 28px (legacy). Compact uses a gradient
  // fill + outer glow to match the v3 spec (`shell.jsx:121-134`); legacy
  // keeps the flat-square look.
  const markSize = compact ? 22 : 28;
  const markFontSize = compact ? '0.625rem' : '0.6875rem';
  const markBg = logoUrl ? 'transparent' : compact ? `linear-gradient(135deg, ${mint}, ${mintSoft})` : mint;
  const markShadow = logoUrl
    ? 'none'
    : compact
      ? `0 0 0 1px ${alpha(mint, 0.3)}, 0 4px 12px -4px ${alpha(mint, 0.9)}`
      : `0 0 0 1px ${alpha(mint, 0.5)}`;

  return (
    <Box
      component={interactive ? 'button' : 'div'}
      {...(interactive ? { type: 'button' as const, onClick } : {})}
      aria-label={interactive ? label : undefined}
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: compact ? 1.125 : 1,
          height: 44,
          py: 0.5,
          pl: 0.5,
          pr: showSwitcher ? 0.5 : 1,
          // v3 compact mode pins the brand cluster to a canonical 196px so
          // it never competes with the breadcrumb (`shell.jsx:126`,
          // specs/00-tokens-and-invariants.md "Layout dimensions: Brand
          // block width 196"). Legacy verbose mode stays auto-width.
          ...(compact ? { width: 196, flexShrink: 0 } : null),
          border: 'none',
          background: 'transparent',
          color: 'inherit',
          fontFamily: 'inherit',
          cursor: interactive ? 'pointer' : 'default',
          borderRadius: 1,
          transition: 'background-color 120ms ease',
          '&:hover': interactive
            ? {
                backgroundColor: 'action.hover',
              }
            : undefined,
          '&:focus-visible': {
            outline: `2px solid ${mint}`,
            outlineOffset: 2,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        aria-hidden
        sx={{
          width: markSize,
          height: markSize,
          borderRadius: 1,
          background: markBg,
          color: theme.palette.success.contrastText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
          fontWeight: 700,
          fontSize: markFontSize,
          letterSpacing: '0.04em',
          overflow: 'hidden',
          flexShrink: 0,
          boxShadow: markShadow,
        }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          resolvedInitials
        )}
      </Box>
      {compact ? (
        // v3 compact: [◆ label • ▾] — single-line label + status dot +
        // chevron, all flush in a 196px wide cluster. Chevron is purely
        // visual when `showSwitcher` is false (the legacy slot below);
        // when true it doubles as the workspace-switcher affordance.
        <>
          <Typography
            component="span"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: 'text.primary',
              lineHeight: 1.2,
              letterSpacing: '-0.005em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              // Cluster math: 196 (block) - 22 (mark) - 9 (gap-after-mark)
              // - 5 (dot) - 5 (gap-before-chevron) - 11 (chevron) - 11
              // (l/r padding) ≈ 133. Use 130 to leave a hair of slack for
              // long tenant names — they'll ellipsize cleanly.
              maxWidth: 130,
              flex: 1,
            }}
          >
            {label}
          </Typography>
          <GlassTooltip title={statusDot?.tooltip ?? ''} placement="bottom" disableHoverListener={!statusDot?.tooltip}>
            <Box
              aria-label={statusDot?.tooltip}
              role={statusDot?.tooltip ? 'img' : undefined}
              sx={{
                // shell.jsx: `width: 4, height: 4` + `boxShadow: 0 0 4px ${mint}`
                // — tiny dot, soft halo. Bumped to 5 to keep visible on hi-dpi.
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: dotColor,
                boxShadow: `0 0 4px ${alpha(dotColor, 0.9)}`,
                flexShrink: 0,
                ml: -0.25,
              }}
            />
          </GlassTooltip>
          {/* Decorative cap chevron — present in v3 compact even when
              `showSwitcher` is false (the legacy slot at the end of this
              component only renders when explicitly enabled). Visual-only
              here: parent button is the actual click target; chevron has
              no separate hit area. */}
          <ExpandMoreIcon aria-hidden sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0, ml: -0.25 }} />
        </>
      ) : (
        // Legacy verbose: 2-line label + mono caption.
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            lineHeight: 1.15,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 180,
            }}
          >
            {label}
          </Typography>
          {caption && (
            <Typography
              variant="overline"
              component="span"
              sx={{
                mt: 0.25,
                fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
                color: isDark ? alpha(theme.palette.text.primary, 0.6) : 'text.secondary',
                letterSpacing: '0.1em',
                lineHeight: 1.1,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {caption}
            </Typography>
          )}
        </Box>
      )}
      {/* Legacy-mode workspace-switcher chevron. In compact mode the
          decorative cap chevron above replaces this — `compact && showSwitcher`
          would otherwise render two chevrons (one cap, one switcher). */}
      {!compact && showSwitcher && <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />}
    </Box>
  );
}
